import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import ApiServices from '../../config/ApiServices';
import ApiEndpoint from '../../config/ApiEndpoint';
import { formatClubType } from '../../Utils/displayLabels';
import { getTimeDifferenceMessage, reverseGeocodeToSuburb } from '../../Utils/func';
import { StatusBadge } from './index';
import Constants from '../../config/Constants';

const UserDetailsModal = ({ open, onClose, userId, role, token, setIsLoaded, locationLabel, hideEmail = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sportsMap, setSportsMap] = useState({});
  const [resolvedLocation, setResolvedLocation] = useState('');

  useEffect(() => {
    if (open && userId && role && token) {
      fetchUserData();
      fetchSportsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, role, token]);

  const fetchSportsList = async () => {
    if (!token) return;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.SPORT_LIST_COMMON,
        {},
        headers
      );
      if (data?.status && Array.isArray(data.data)) {
        // Create a map of sport ID to sport name for quick lookup
        const map = {};
        data.data.forEach(sport => {
          map[sport.id] = sport.name;
        });
        setSportsMap(map);
      }
    } catch (error) {
      console.error("Failed to fetch sports list", error);
    }
  };

  const getFallbackLocation = () => {
    if (!userData) return '';

    // Prefer userSchoolAddress suburb if available
    if (userData.userSchoolAddress && userData.userSchoolAddress.suburb) {
      return userData.userSchoolAddress.suburb;
    }

    // Generic suburb/city-style fields (user_suburb set by backend on save)
    const genericSuburb =
      userData.user_suburb ||
      userData.city ||
      userData.town ||
      userData.suburb ||
      userData.locality ||
      userData.user_city ||
      '';

    if (genericSuburb) {
      return genericSuburb;
    }

    // Fallback to raw address string if nothing else
    const genericAddress =
      userData.user_address ||
      userData.address ||
      userData.location ||
      '';

    return genericAddress || '';
  };

  // Resolve location used in the modal:
  // 1) Prefer label passed from list (already reverse-geocoded there)
  // 2) Fallback to structured address fields
  // 3) If still empty, reverse-geocode lat/lng from profile response
  useEffect(() => {
    const explicit = locationLabel && locationLabel.trim();
    if (explicit) {
      setResolvedLocation(explicit);
      return;
    }

    const fallback = getFallbackLocation();
    if (fallback) {
      setResolvedLocation(fallback);
      return;
    }

    if (!userData) return;

    const lat = parseFloat(userData.user_latitude || userData.latitude);
    const lng = parseFloat(userData.user_longitude || userData.longitude);
    const apiKey = Constants.MAP_API_KEY;

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return;
    }

    let cancelled = false;

    const fetchLocation = async () => {
      try {
        const display = await reverseGeocodeToSuburb(lat, lng, apiKey);
        if (!cancelled) setResolvedLocation(display);
      } catch (error) {
        console.error('Reverse geocoding failed (profile modal):', error);
        if (!cancelled) setResolvedLocation(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      }
    };

    fetchLocation();

    return () => {
      cancelled = true;
    };
  }, [locationLabel, userData]);

  const fetchUserData = async () => {
    if (!token || !userId || !role) return;
    
    let body = {};
    let endpoint;
    
    // Handle different roles properly
    if (role === "referee" || role === "coach" || role === "player" || role === "parent") {
      body = { id_umphire: userId };
      endpoint = ApiEndpoint.USER_PROFILE;
    } else if (role === "schools" || role === "individual") {
      body = { id_organisation: userId };
      endpoint = ApiEndpoint.ORG_PROFILE;
    } else {
      // Default fallback
      body = { id_umphire: userId };
      endpoint = ApiEndpoint.USER_PROFILE;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };

    setLoading(true);
    setIsLoaded?.(true);
    
    try {
      const data = await ApiServices.PostApiCall(
        endpoint,
        JSON.stringify(body),
        headers
      );

      setLoading(false);
      setIsLoaded?.(false);

      if (data?.status) {
        setUserData(data.data);
      } else {
        setUserData(null);
      }
    } catch (error) {
      setLoading(false);
      setIsLoaded?.(false);
      setUserData(null);
    }
  };

  const handleClose = () => {
    setUserData(null);
    onClose();
  };

  if (!open) return null;

  const isOrg = role === "schools" || role === "individual";
  const displayName = isOrg
    ? userData?.organisationDetails?.name
    : `${userData?.first_name || ""} ${userData?.last_name || ""}`;
  const displayImage = isOrg
    ? userData?.organisationDetails?.logo_url
    : userData?.profile_image;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
          margin: { xs: 2, sm: 2 },
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #e2e8f0',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: isDark ? '#ffffff' : '#1a202c',
          }}
        >
          User Profile
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: isDark ? '#cbd5e1' : '#718096',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f7fafc',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography sx={{ color: isDark ? '#cbd5e1' : '#718096' }}>
              Loading...
            </Typography>
          </Box>
        ) : !userData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography sx={{ color: isDark ? '#cbd5e1' : '#718096' }}>
              No user data found
            </Typography>
          </Box>
        ) : (
          <>
            {/* Profile Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                src={displayImage}
                sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                  color: isDark ? '#818cf8' : '#1976d2',
                  fontSize: '2rem',
                  fontWeight: 700,
                }}
              >
                {displayName?.[0]?.toUpperCase()}
              </Avatar>
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: isDark ? '#ffffff' : '#1a202c',
                  mb: 0.5,
                }}
              >
                {displayName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: isDark ? '#cbd5e1' : '#718096',
                  mb: 1,
                }}
              >
                Joined {role === "individual" ? "Club" : role}{" "}
                {userData?.createdAt && getTimeDifferenceMessage(new Date(userData.createdAt))}
              </Typography>
              {userData?.status && (
                <StatusBadge status={userData.status} />
              )}
            </Box>

            <Divider sx={{ mb: 3, borderColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#e2e8f0' }} />

            {/* Personal Info */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: isDark ? '#ffffff' : '#1a202c',
                  mb: 2,
                }}
              >
                Personal Info
              </Typography>
              <Box
                sx={{
                  backgroundColor: isDark ? '#242424' : '#f7fafc',
                  borderRadius: '12px',
                  p: 2.5,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {!hideEmail && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: isDark ? '#cbd5e1' : '#718096',
                          mb: 0.75,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Email Address
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: isDark ? '#ffffff' : '#1a202c',
                          fontWeight: 500,
                        }}
                      >
                        {userData?.email || '-'}
                      </Typography>
                    </Box>
                  )}
                  {userData?.phone_number && userData.phone_number !== '' && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: isDark ? '#cbd5e1' : '#718096',
                          mb: 0.75,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Phone Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: isDark ? '#ffffff' : '#1a202c',
                          fontWeight: 500,
                        }}
                      >
                        {userData.phone_number}
                      </Typography>
                    </Box>
                  )}
                  {userData?.sport && userData.sport !== '' && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: isDark ? '#cbd5e1' : '#718096',
                          mb: 0.75,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Sport
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: isDark ? '#ffffff' : '#1a202c',
                          fontWeight: 500,
                        }}
                      >
                        {userData.sport}
                      </Typography>
                    </Box>
                  )}
                  {resolvedLocation && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: isDark ? '#cbd5e1' : '#718096',
                          mb: 0.75,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Location
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: isDark ? '#ffffff' : '#1a202c',
                          fontWeight: 500,
                          wordBreak: 'break-word',
                        }}
                      >
                        {resolvedLocation}
                      </Typography>
                    </Box>
                  )}
                  {userData?.userSchoolAddress && (
                    <>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: isDark ? '#cbd5e1' : '#718096',
                            mb: 0.75,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Post Code
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: isDark ? '#ffffff' : '#1a202c',
                            fontWeight: 500,
                          }}
                        >
                          {userData.userSchoolAddress.post_code || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: isDark ? '#cbd5e1' : '#718096',
                            mb: 0.75,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Street
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: isDark ? '#ffffff' : '#1a202c',
                            fontWeight: 500,
                          }}
                        >
                          {userData.userSchoolAddress.street_name || '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: isDark ? '#cbd5e1' : '#718096',
                            mb: 0.75,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Suburb
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: isDark ? '#ffffff' : '#1a202c',
                            fontWeight: 500,
                          }}
                        >
                          {userData.userSchoolAddress.suburb || '-'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Bio */}
            {userData?.bio && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: isDark ? '#ffffff' : '#1a202c',
                    mb: 1.5,
                  }}
                >
                  Bio
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: isDark ? '#cbd5e1' : '#4a5568',
                    lineHeight: 1.6,
                  }}
                >
                  {userData.bio}
                </Typography>
              </Box>
            )}

            {/* Sports */}
            {(() => {
              // Collect all sports from different possible fields
              const allSports = [];
              
              // Check sportList (array of objects with name property)
              if (userData?.sportList && Array.isArray(userData.sportList) && userData.sportList.length > 0) {
                userData.sportList.forEach(sport => {
                  if (sport && (sport.name || sport)) {
                    allSports.push(sport.name || sport);
                  }
                });
              }
              
              // Check userSchoolSport (array of objects with name property)
              if (userData?.userSchoolSport && Array.isArray(userData.userSchoolSport) && userData.userSchoolSport.length > 0) {
                userData.userSchoolSport.forEach(sport => {
                  if (sport && (sport.name || sport)) {
                    allSports.push(sport.name || sport);
                  }
                });
              }
              
              // Check sportIdList (array of IDs) - convert to names using sportsMap
              if (userData?.sportIdList && Array.isArray(userData.sportIdList) && userData.sportIdList.length > 0) {
                userData.sportIdList.forEach(sportId => {
                  const sportName = sportsMap[sportId];
                  if (sportName) {
                    allSports.push(sportName);
                  }
                });
              }
              
              // Remove duplicates
              const uniqueSports = [...new Set(allSports)];
              
              return uniqueSports.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: isDark ? '#ffffff' : '#1a202c',
                      mb: 1.5,
                    }}
                  >
                    Sports
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {uniqueSports.map((sportName, index) => (
                      <Chip
                        key={`sport-${index}`}
                        label={sportName}
                        size="small"
                        sx={{
                          backgroundColor: isDark
                            ? 'rgba(99, 102, 241, 0.15)'
                            : 'rgba(25, 118, 210, 0.1)',
                          color: isDark ? '#818cf8' : '#1976d2',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : null;
            })()}

            {/* Recommended By */}
            {userData?.recommendedList && userData.recommendedList.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: isDark ? '#ffffff' : '#1a202c',
                    mb: 1.5,
                  }}
                >
                  Recommended By
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {userData.recommendedList.map((org, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: '8px',
                        backgroundColor: isDark ? '#242424' : '#f7fafc',
                      }}
                    >
                      <Avatar
                        src={org.logo_url}
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: isDark ? '#ffffff' : '#1a202c',
                          }}
                        >
                          {org.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: isDark ? '#cbd5e1' : '#718096',
                          }}
                        >
                          {formatClubType(org.school_type)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsModal;
